import React, { useEffect, useState } from 'react';
import { FiGlobe, FiRefreshCw, FiCheck, FiCopy, FiAlertCircle, FiAlertTriangle } from 'react-icons/fi';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import api from '../../../../api/api';
import { MailerooDomainResult } from '../../../../model/MailerooDomainResult';
import { toast } from '../../Toast';
import { SectionProps } from './AnnouncementSettings';

interface Props extends SectionProps {
    // the saved From Email: the records are for the saved domain, not one still being typed
    savedFrom: string;
    checking: boolean;
    setChecking: (checking: boolean) => void;
}

// pre-configured by Investi, so there are no records for the client to add (v1 hides its DNS records link too)
const SHARED_DOMAIN = 'investi-mailer.com.au';

// the part after @, as v1 does for its DNS records link
const domainOf = (email: string) => {
    const e = email.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e.split('@')[1].toLowerCase() : '';
};

const percent = (n: number, total: number) => total ? `${(n * 100 / total).toFixed(1)}%` : '—';

const Domain: React.FC<Props> = ({ form, setForm, savedFrom, checking, setChecking }) => {
    const [result, setResult] = useState<MailerooDomainResult>();
    const set = (field: keyof typeof form.domain, value: string) => setForm(f => ({ ...f, domain: { ...f.domain, [field]: value } }));
    const sendingDomain = domainOf(form.domain.from);
    const savedDomain = domainOf(savedFrom);
    const shared = savedDomain === SHARED_DOMAIN;

    const load = async () => {
        if (!savedDomain || shared) {
            setResult(undefined);
            return;
        }
        setChecking(true);
        try {
            const r = await api().getMailerooDomain(savedDomain);
            setResult(r);
        } catch {
            setResult({ domain: savedDomain, found: false, active: false, records: [], delivered: 0, bounced: 0, message: 'Could not reach the server. Please try again.' });
        } finally {
            setChecking(false);
        }
    };

    // Maileroo only queues the scan, so the table keeps the old status until the page is reloaded
    const scan = async () => {
        setChecking(true);
        try {
            const r = await api().scanMailerooDomain(savedDomain);
            if (r.queued)
                toast.success('DNS check queued — results update in a few minutes');
            else
                toast.error(r.message || 'Could not start the DNS check');
        } catch {
            toast.error('Could not reach the server. Please try again.');
        } finally {
            setChecking(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [savedDomain]);

    // leaving the Domain section clears the nav's "Checking…" line
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => setChecking(false), []);

    const copy = (text: string, what: string) => {
        navigator.clipboard?.writeText(text).then(
            () => toast.success(`Copied the ${what}`),
            () => toast.error('Could not copy — select it and copy it by hand'));
    };

    const records = result?.found ? result.records : [];
    const verified = records.filter(r => r.verified).length;
    const delivered = result?.delivered ?? 0;
    const bounced = result?.bounced ?? 0;

    const checkButton = (cls: string) => <span className={cls}>
        <button type="button" className="btn-outline with-icon" onClick={scan} disabled={checking || !savedDomain || shared}>
            <FiRefreshCw className={checking ? 'spin' : ''} /> {checking ? 'Checking…' : 'Check DNS Again'}
        </button>
    </span>;

    const banner = (cls: string, icon: React.ReactNode, title: string, sub?: string) => <div className={`domain-status ${cls}`}>
        <span className="icon-tile">{icon}</span>
        <div className="text"><strong>{title}</strong>{sub && <span>{sub}</span>}</div>
    </div>;

    const status = () => {
        if (shared) return banner('ok', <FiCheck />, 'Sending from Investi’s shared domain — nothing to set up');
        if (!savedDomain) return null;
        if (checking) return banner('checking', <FiRefreshCw className="spin" />, 'Checking your DNS records…', 'Asking Maileroo');
        if (!result) return null;
        if (result.message) return banner('bad', <FiAlertTriangle />, result.message);
        if (!result.found) return banner('warn', <FiAlertCircle />, `${savedDomain} isn’t set up in Maileroo yet — ask Investi to add it`);
        const sub = `${verified} of ${records.length} records verified`;
        if (result.active) return banner('ok', <FiCheck />, `Domain verified — sending from ${savedFrom}`, sub);
        return banner('bad', <FiAlertTriangle />, `Domain inactive in Maileroo — ${savedDomain} can’t send yet`, sub);
    };

    return <>
        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiGlobe /></span>
                <div className="text">
                    <h2>Send from Your Own Domain</h2>
                    <div className="hint">Investi sends through Maileroo. These records prove the domain is yours.</div>
                </div>
                {checkButton('in-title')}
            </div>

            {status()}

            <div className="fields three">
                <label className="field">
                    <span className="label">Sending domain</span>
                    <input value={sendingDomain} readOnly />
                </label>
                <label className="field">
                    <span className="label">Emails come from</span>
                    <input type="email" value={form.domain.from} onChange={e => set('from', e.target.value)} />
                </label>
                <label className="field">
                    <span className="label">From name</span>
                    <input value={form.domain.fromName} placeholder="Your company name" onChange={e => set('fromName', e.target.value)} />
                </label>
            </div>

            {records.length > 0 && <>
                <strong className="records-title">DNS records to add at your domain provider</strong>
                <div className="dns">
                    <div className="tr th">
                        <span className="type">Type</span>
                        <span className="host">Name / host</span>
                        <span className="value">Value</span>
                        <span className="status">Status</span>
                        <span className="copy" />
                    </div>
                    {records.map(r => <div key={r.name} className="tr">
                        <span className="type"><span className="badge plain">{r.type}</span></span>
                        <span className="host">
                            <span className="mono" title={r.hostname}>{r.hostname}</span>
                            <button type="button" className="icon-btn small" title="Copy name / host" aria-label={`Copy the ${r.name} host`} onClick={() => copy(r.hostname, `${r.name} host`)}><FiCopy /></button>
                        </span>
                        <span className="value mono" title={r.value}>{r.value}</span>
                        <span className={`status ${r.verified ? 'found' : 'unverified'}`}>
                            {r.verified ? <><FiCheck /> Verified</> : <><FiAlertCircle /> Unverified</>}
                        </span>
                        <span className="copy"><button type="button" className="icon-btn" title="Copy value" aria-label={`Copy the ${r.name} record`} onClick={() => copy(r.value, `${r.name} value`)}><FiCopy /></button></span>
                    </div>)}
                </div>
                {checkButton('at-bottom')}
            </>}
        </section>

        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><ShieldCheckIcon /></span>
                <div className="text"><h2>Sending Health</h2><div className="hint">All time</div></div>
            </div>
            <div className="health">
                <div><span>Delivered</span><strong>{percent(delivered, delivered + bounced)} · {delivered.toLocaleString()}</strong></div>
                <div><span>Bounced</span><strong>{percent(bounced, delivered + bounced)} · {bounced.toLocaleString()}</strong></div>
                <div><span>Provider</span><strong>Maileroo</strong></div>
            </div>
        </section>
    </>;
};

export default Domain;
